import { useState } from 'react';
import { ReliabilityBadge, api, useToast } from './_barrel.js';


        // One incident row: collapsed header (title, grade, source, severity,
        // status, date) that expands to the detail with the analyst controls.
        // `subjectLabel` is shown under the title on cross-subject lists (the
        // register); the subject detail page omits it.
        function IncidentRow({ incident, token, onSignoff, subjectLabel }) {
            const [expanded, setExpanded] = useState(false);
            const [detail, setDetail] = useState(null);
            const [comment, setComment] = useState('');
            const [commentDirty, setCommentDirty] = useState(false);
            const [savingComment, setSavingComment] = useState(false);
            const [savingAck, setSavingAck] = useState(false);
            const [savingInclude, setSavingInclude] = useState(false);
            const [savingRel, setSavingRel] = useState(false);
            const [savingInfo, setSavingInfo] = useState(false);
            const toast = useToast();

            const toggle = async () => {
                if (!expanded && !detail) {
                    try {
                        const d = await api(`/api/v1/incidents/${incident.id}`, {}, token);
                        setDetail(d);
                        setComment(d.analyst_comment || '');
                        setCommentDirty(false);
                    } catch (e) { console.error(e); }
                }
                setExpanded(!expanded);
            };

            const saveComment = async (e) => {
                e?.stopPropagation();
                setSavingComment(true);
                try {
                    const updated = await api(`/api/v1/incidents/${incident.id}`,
                        { method: 'PATCH', body: JSON.stringify({ analyst_comment: comment }) }, token);
                    setDetail(updated);
                    setComment(updated.analyst_comment || '');
                    setCommentDirty(false);
                    toast('Analyst comment saved');
                } catch (err) {
                    toast('Save failed: ' + err.message);
                } finally { setSavingComment(false); }
            };

            const toggleInclude = async (e) => {
                e?.stopPropagation();
                const next = !(detail.include_in_report !== false);
                setSavingInclude(true);
                try {
                    const updated = await api(`/api/v1/incidents/${incident.id}`,
                        { method: 'PATCH', body: JSON.stringify({ include_in_report: next }) }, token);
                    setDetail(updated);
                    toast(next ? 'Note will appear in the report' : 'Note excluded from the report');
                } catch (err) {
                    toast('Failed: ' + err.message);
                } finally { setSavingInclude(false); }
            };

            const setReliability = async (val) => {
                setSavingRel(true);
                try {
                    const updated = await api(`/api/v1/incidents/${incident.id}`,
                        { method: 'PATCH', body: JSON.stringify({ reliability_override: val || null }) }, token);
                    setDetail(updated);
                    toast(val ? `Source reliability overridden to ${val}` : 'Source reliability set to Auto');
                } catch (err) {
                    toast('Failed: ' + err.message);
                } finally { setSavingRel(false); }
            };

            const setInfoCredibility = async (val) => {
                setSavingInfo(true);
                try {
                    const updated = await api(`/api/v1/incidents/${incident.id}`,
                        { method: 'PATCH', body: JSON.stringify({ info_override: val ? Number(val) : null }) }, token);
                    setDetail(updated);
                    toast(val ? `Information credibility overridden to ${val}` : 'Information credibility set to Auto');
                } catch (err) {
                    toast('Failed: ' + err.message);
                } finally { setSavingInfo(false); }
            };

            const toggleAcknowledge = async (e) => {
                e?.stopPropagation();
                setSavingAck(true);
                try {
                    const updated = await api(`/api/v1/incidents/${incident.id}`,
                        { method: 'PATCH', body: JSON.stringify({ acknowledged: !detail.acknowledged_at }) }, token);
                    setDetail(updated);
                    toast(updated.acknowledged_at ? 'Acknowledged' : 'Acknowledgement cleared');
                } catch (err) {
                    toast('Acknowledge failed: ' + err.message);
                } finally { setSavingAck(false); }
            };

            // The effective grade: the fresh detail (after an override change)
            // when we have it, otherwise the list row the server graded.
            const graded = (detail && detail.reliability) ? detail : incident;
            const source = (detail && detail.source) || incident.source;

            return (
                <div className="border-b border-veil last:border-b-0">
                    <div className="px-6 py-4 grid grid-cols-12 gap-4 items-center cursor-pointer hover:bg-[var(--surface-2)]" onClick={toggle}>
                        <div className="col-span-5">
                            <div className="font-display text-lg">{incident.title}</div>
                            {subjectLabel && <div className="text-xs gold">{subjectLabel}</div>}
                            <div className="text-xs text-muted font-mono flex items-center gap-2 flex-wrap">
                                <span>{incident.incident_type_code}</span>
                                {graded.reliability && <ReliabilityBadge c={graded} />}
                                {source && (
                                    <span className="truncate" style={{maxWidth: '280px'}} title={`Source: ${source}`}>· {source}</span>
                                )}
                                {!source && graded.reliability === 'F' && (
                                    <span className="text-dim italic">· no source recorded</span>
                                )}
                                {incident.llm_classification && (
                                    <span className="gold">· AI-classified ({incident.llm_confidence})</span>
                                )}
                            </div>
                            {incident.llm_reasoning && (
                                <div className="text-xs text-muted italic mt-1 leading-snug">
                                    {incident.llm_reasoning}
                                </div>
                            )}
                        </div>
                        <div className="col-span-2"><span className={`label-tag sev-${incident.severity}`}>{incident.severity}</span></div>
                        <div className="col-span-2 text-xs font-mono text-muted">{incident.status.replace('_',' ')}</div>
                        <div className="col-span-2 text-xs font-mono text-dim">{new Date(incident.detected_at).toLocaleDateString('en-GB')}</div>
                        <div className="col-span-1 text-right text-xs text-muted">{expanded ? '−' : '+'}</div>
                    </div>
                    {expanded && detail && (
                        <div className="px-6 pb-5 bg-[var(--bg)]" onClick={(e) => e.stopPropagation()}>
                            <div className="text-sm leading-relaxed mb-4">{detail.summary}</div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs mb-4">
                                <div className="text-muted">Confidence</div><div>{detail.confidence}</div>
                                {detail.source && (<><div className="text-muted">Source</div><div>{detail.source}</div></>)}
                                {detail.occurred_at && (<><div className="text-muted">Occurred</div><div>{new Date(detail.occurred_at).toLocaleString('en-GB')}</div></>)}
                                <div className="text-muted">Detected</div><div>{new Date(detail.detected_at).toLocaleString('en-GB')}</div>
                                <div className="text-muted" title="Admiralty source-reliability grade shown against this source in the report. 'Auto' derives it from the source type; override if your judgement differs.">Source reliability</div>
                                <div>
                                    <select value={detail.reliability_override || ''} disabled={savingRel}
                                        onChange={(e) => setReliability(e.target.value)}
                                        style={{background:'var(--zone-2)', color:'var(--text)', border:'1px solid var(--veil)', padding:'2px 6px', fontSize:'12px'}}>
                                        <option value="">Auto</option>
                                        <option value="A">A — Reliable</option>
                                        <option value="B">B — Usually reliable</option>
                                        <option value="C">C — Fairly reliable</option>
                                        <option value="D">D — Not usually reliable</option>
                                        <option value="E">E — Unreliable</option>
                                        <option value="F">F — Cannot be judged</option>
                                    </select>
                                    {detail.reliability_override && <span className="ml-2 text-xs text-muted">analyst-set</span>}
                                </div>
                                <div className="text-muted" title="Admiralty information-credibility digit (1–6) shown against this source in the report. 'Auto' derives it from corroboration; override if your judgement differs.">Information credibility</div>
                                <div>
                                    <select value={detail.info_override || ''} disabled={savingInfo}
                                        onChange={(e) => setInfoCredibility(e.target.value)}
                                        style={{background:'var(--zone-2)', color:'var(--text)', border:'1px solid var(--veil)', padding:'2px 6px', fontSize:'12px'}}>
                                        <option value="">Auto</option>
                                        <option value="1">1 — Confirmed</option>
                                        <option value="2">2 — Probably true</option>
                                        <option value="3">3 — Possibly true</option>
                                        <option value="4">4 — Doubtful</option>
                                        <option value="5">5 — Improbable</option>
                                        <option value="6">6 — Cannot be judged</option>
                                    </select>
                                    {detail.info_override && <span className="ml-2 text-xs text-muted">analyst-set</span>}
                                </div>
                            </div>

                            {/* Analyst comment editor */}
                            <div className="mb-4 p-3" style={{background:'rgba(201,169,110,0.05)', border:'1px solid rgba(201,169,110,0.20)'}}>
                                <div className="flex justify-between items-baseline mb-2">
                                    <div className="text-xs font-mono uppercase tracking-widest gold">Analyst Note</div>
                                    {detail.analyst_comment_at && (
                                        <div className="text-xs text-dim font-mono">
                                            last edited {new Date(detail.analyst_comment_at).toLocaleString('en-GB')}
                                        </div>
                                    )}
                                </div>
                                <textarea
                                    rows="3"
                                    value={comment}
                                    onChange={(e) => { setComment(e.target.value); setCommentDirty(true); }}
                                    placeholder="Write your own observation on this incident. This appears in the client PDF, attributed to you. Keep it factual and specific."
                                    style={{width:'100%', resize:'vertical', background:'var(--zone-2)', color:'var(--text)', border:'1px solid var(--veil)', padding:'8px', fontFamily:'inherit', fontSize:'13px'}}
                                />
                                <div className="flex justify-between items-center mt-2 gap-2">
                                    <label className="flex items-center gap-2 text-xs font-mono cursor-pointer"
                                        title="When ticked, this note/assessment is carried into the subject report. Untick to keep it out."
                                        style={{color: (detail.include_in_report !== false) ? 'var(--gold)' : 'var(--text-muted)'}}
                                        onClick={(e) => e.stopPropagation()}>
                                        <input type="checkbox" checked={detail.include_in_report !== false}
                                            disabled={savingInclude} onChange={toggleInclude} />
                                        Include in report
                                    </label>
                                    <div className="flex items-center gap-2">
                                        {commentDirty && <span className="text-xs text-muted font-mono self-center">unsaved</span>}
                                        <button type="button" className="btn-ghost" onClick={saveComment} disabled={savingComment || !commentDirty}
                                            style={{fontSize:'11px', padding:'5px 12px'}}>
                                            {savingComment ? 'Saving…' : 'Save Note'}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {detail.signoff_events.length > 0 && (
                                <div className="mt-4">
                                    <div className="text-xs font-mono uppercase tracking-widest text-muted mb-2">Sign-off Chain</div>
                                    <div className="space-y-1">
                                        {detail.signoff_events.map(e => (
                                            <div key={e.id} className="text-xs flex items-baseline gap-3">
                                                <span className="font-mono text-dim w-6">#{e.sequence_no}</span>
                                                <span className="font-mono">{e.from_status ?? 'init'} → <span className="gold">{e.to_status}</span></span>
                                                <span className="text-muted">by {e.signed_by_display_name}</span>
                                                {e.reason && <span className="text-muted italic">"{e.reason}"</span>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="flex justify-between items-center mt-4">
                                <div className="text-xs text-muted font-mono">
                                    {detail.acknowledged_at
                                        ? `Acknowledged ${new Date(detail.acknowledged_at).toLocaleString('en-GB')}`
                                        : 'Not yet acknowledged'}
                                </div>
                                <div className="flex gap-2">
                                    <button type="button" className="btn-ghost" onClick={toggleAcknowledge} disabled={savingAck}
                                        style={{fontSize:'11px', padding:'5px 12px'}}>
                                        {savingAck ? 'Saving…' : (detail.acknowledged_at ? 'Clear Acknowledgement' : 'Acknowledge ✓')}
                                    </button>
                                    {!['closed','false_positive'].includes(detail.status) && (
                                        <button className="btn-ghost" onClick={(e) => { e.stopPropagation(); onSignoff(); }}>Sign Off Transition →</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

export { IncidentRow };
